/* eslint-disable @typescript-eslint/require-await */
"use client";

import { useCallback, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";

import { useMap } from "react-map-gl/maplibre";

import { useBoundariesEditGeometryCache } from "@/app/(internal)/panel/(maps)/_contexts/boundaries-edit-geometry-cache-context";
import { geometryGeoJsonToPolygon } from "@/app/(internal)/panel/(maps)/map/_utils/geometry-geojson-to-polygon";
import {
  propertyIdToString,
  readPreviewBoundaryFeatureProperties,
} from "@/app/(internal)/panel/reports/preview-map/_utils/parse-preview-boundary-feature-ids";
import {
  findDraftFeatureIndex,
  getPreviewBoundaryId,
  isValidPreviewPolygonGeometry,
} from "@/app/(internal)/panel/reports/preview-map/_utils/preview-boundary-details.utils";
import {
  startDraftPolygonEditWhenReady,
  syncDraftSourceToMap,
} from "@/app/(internal)/panel/reports/preview-map/_utils/preview-boundary-draft-source.utils";
import { dictionaries } from "@/dictionaries/content";
import { MAP_LEVEL_DRAFT_SOURCE_ID } from "@/providers/maps/constants";
import { useBoundaryDraftDirtyState } from "@/providers/maps/hooks/boundaries/use-boundary-draft-dirty-state";
import type { PolygonData, PolygonIdentifier, PolygonsLayerRef } from "@/providers/maps/types";
import { BaselineCompareMode } from "@/providers/maps/utils/boundary-draft-baseline.utils";
import { buildMapLevelBoundaryDraftFeatureFromLive } from "@/providers/maps/utils/build-boundary-draft-feature-from-live.utils";
import { mapFeaturesToGapFillDraftOverrides } from "@/providers/maps/utils/gap-fill-draft-overrides.utils";
import { ApiTags } from "@/providers/store/api/api-tags.enum";
import type { IGapFillDraftOverrides } from "@/providers/store/api/custom-layers/custom-layers.interface";
import mapLevelsApi, {
  useGetMapLevelQuery,
  useUpsertMapLevelBoundariesMutation,
} from "@/providers/store/api/map-levels/map-levels";
import {
  toMapLevelBoundaryDescriptionUpsertValue,
  type IUpsertMapLevelBoundaryItem,
} from "@/providers/store/api/map-levels/map-levels.interface";
import { useAppDispatch } from "@/providers/store/hooks";
import { useToast } from "@/providers/toasts/toasts.provider";
import {
  BOUNDARY_UPSERT_MAX_BATCH_BYTES,
  chunkByPayloadSize,
  estimateJsonByteSize,
  runSequentialBatches,
} from "@/shared/utils/chunk-by-payload-size.util";

import type maplibregl from "maplibre-gl";

const randomHex = () => {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

export const MAP_LEVEL_DRAFT_NEW_ID_PREFIX = "draft-";

const BASELINE_COMPARE_MODE = BaselineCompareMode.WithMapLevelMetadata;

const EMPTY_FEATURE_COLLECTION: GeoJSON.FeatureCollection<GeoJSON.Polygon> = {
  type: "FeatureCollection",
  features: [],
};

function toVisibleDraftGeoJson(
  features: GeoJSON.Feature<GeoJSON.Polygon>[],
): GeoJSON.FeatureCollection<GeoJSON.Polygon> {
  const visible = features.filter((f) => !f.properties?.deleted);
  return visible.length > 0
    ? { type: "FeatureCollection", features: visible }
    : EMPTY_FEATURE_COLLECTION;
}

function isNewDraftId(id: string): boolean {
  return id.startsWith(MAP_LEVEL_DRAFT_NEW_ID_PREFIX);
}

/** Boundary id from tile/draft feature click: `properties.id` (nested-aware) or the polygon id. */
function parseMapLevelBoundaryFeatureId(polygon: PolygonData): string | null {
  const raw = (polygon.properties ?? {}) as Record<string, unknown>;
  const properties = readPreviewBoundaryFeatureProperties(raw);

  const boundaryId =
    propertyIdToString(properties.id) ||
    propertyIdToString(polygon.id) ||
    propertyIdToString(polygon.value);

  return boundaryId || null;
}

/** featureFromPreviewGeometry-analog for map level boundaries (id + mapLevelId props). */
function featureFromMapLevelGeometry(params: {
  id: string;
  geometryGeoJson: string;
  mapLevelId: string;
  baseFeature?: GeoJSON.Feature<GeoJSON.Polygon> | null;
  /** Saved name/description from GET map-level (tiles/cache are geometry-only). */
  savedMeta?: { name?: string | null; description?: string | null } | null;
}): GeoJSON.Feature<GeoJSON.Polygon> {
  const geometry = geometryGeoJsonToPolygon(params.geometryGeoJson);
  const rawProps = (params.baseFeature?.properties ?? {}) as Record<string, unknown>;
  const props = readPreviewBoundaryFeatureProperties(rawProps);
  const savedName =
    typeof params.savedMeta?.name === "string" && params.savedMeta.name.trim() !== ""
      ? params.savedMeta.name
      : undefined;
  const savedDescription =
    typeof params.savedMeta?.description === "string" ? params.savedMeta.description : undefined;
  const tileName = typeof props.name === "string" && props.name !== "" ? props.name : undefined;
  const tileDescription =
    typeof props.description === "string" ? props.description : undefined;

  // Prefer tile props when present; otherwise hydrate from REST meta for dirty baseline.
  let nameProps: { name: string } | Record<string, never> = {};
  if (tileName != null) {
    nameProps = { name: tileName };
  } else if (savedName != null) {
    nameProps = { name: savedName };
  }

  let descriptionProps: { description: string } | Record<string, never> = {};
  if (tileDescription != null) {
    descriptionProps = { description: tileDescription };
  } else if (savedDescription != null) {
    descriptionProps = { description: savedDescription };
  }

  return {
    type: "Feature",
    id: params.id,
    geometry,
    properties: {
      ...props,
      id: params.id,
      mapLevelId: propertyIdToString(props.mapLevelId) || params.mapLevelId,
      ...nameProps,
      ...descriptionProps,
    },
  };
}

function descriptionForUpsert(
  props: Record<string, unknown> | undefined,
): string | null | undefined {
  if (props == null || !("description" in props)) {
    return undefined;
  }
  return toMapLevelBoundaryDescriptionUpsertValue(
    typeof props.description === "string" ? props.description : "",
  );
}

export interface MapLevelDraftSelection {
  boundaryId: string;
  mapLevelId: string;
}

function selectionFromFeature(
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  fallbackMapLevelId: string,
): MapLevelDraftSelection | null {
  const boundaryId = getPreviewBoundaryId(feature);
  if (!boundaryId) {
    return null;
  }

  const props = feature.properties as Record<string, unknown> | undefined;
  const mapLevelId = propertyIdToString(props?.mapLevelId) || fallbackMapLevelId;

  return { boundaryId, mapLevelId };
}

export interface UseMapLevelBoundariesDraftOptions {
  mapLevelBoundariesLayerRef: RefObject<PolygonsLayerRef | null>;
  mapLevelDraftLayerRef: RefObject<PolygonsLayerRef | null>;
  districtId: string;
  mapLevelId: string;
  onSaveSuccess?: () => void;
}

export interface UseMapLevelBoundariesDraftReturn {
  draftGeoJson: GeoJSON.FeatureCollection<GeoJSON.Polygon>;
  hasDraftChanges: boolean;
  getHasDraftChanges: () => boolean;
  draftBoundaryIds: string[];
  getTileLayerFilter: () => maplibregl.FilterSpecification | undefined;
  upsertToDraft: (feature: GeoJSON.Feature<GeoJSON.Polygon>) => void;
  addDrawnFeatureToDraft: (
    feature: GeoJSON.Feature<GeoJSON.Polygon>,
    onAdded?: (newFeatureId: string) => void,
  ) => void;
  deleteInDraft: (
    polygonId: string | number,
    onAfter?: (nextVisible: GeoJSON.FeatureCollection<GeoJSON.Polygon>) => void,
  ) => void;
  removeFromDraft: (boundaryId: string) => void;
  clearDraft: () => void;
  clearDraftLayerActiveState: (options?: { endDrawSession?: boolean }) => void;
  saveDraft: () => Promise<void>;
  persistCurrentEditing: () => void;
  ensureBoundaryInDraftFromId: (
    boundaryId: string,
    lookup?: { mapPolygonId?: string | number },
  ) => Promise<MapLevelDraftSelection | null>;
  handleTilePolygonClick: (
    polygonData: PolygonData,
    event: maplibregl.MapMouseEvent,
  ) => Promise<MapLevelDraftSelection | null>;
  handleDraftPolygonClick: (
    polygonData: PolygonData,
    event: maplibregl.MapMouseEvent,
  ) => Promise<MapLevelDraftSelection | null>;
  getDraftFeatureByBoundaryId: (boundaryId: string) => GeoJSON.Feature<GeoJSON.Polygon> | null;
  syncDraftSourceToMap: () => void;
  handlePolygonEditStart: (identifier: PolygonIdentifier) => void;
  handleDrawUpdate: (polygon: GeoJSON.Feature) => void;
  markBoundaryUserTouched: (boundaryId: string | number) => void;
  /** Draft-only Name/Note update (same role as updateBoundarySchool on work map). */
  updateBoundaryMetadata: (boundaryId: string, patch: { name?: string; note?: string }) => void;
  getGapFillDraftOverrides: () => IGapFillDraftOverrides;
}

export function useMapLevelBoundariesDraft(
  options: UseMapLevelBoundariesDraftOptions,
): UseMapLevelBoundariesDraftReturn {
  const {
    mapLevelBoundariesLayerRef,
    mapLevelDraftLayerRef,
    districtId,
    mapLevelId,
    onSaveSuccess,
  } = options;

  const mapRef = useMap();
  const dispatch = useAppDispatch();
  const { getGeometry: getCachedGeometry } = useBoundariesEditGeometryCache();
  const [upsertBoundaries] = useUpsertMapLevelBoundariesMutation();
  const { showToast } = useToast();
  const { data: mapLevel } = useGetMapLevelQuery({
    districtId,
    levelId: mapLevelId,
  });
  const [draftFeatures, setDraftFeatures] = useState<GeoJSON.Feature<GeoJSON.Polygon>[]>([]);
  const draftFeaturesRef = useRef<GeoJSON.Feature<GeoJSON.Polygon>[]>([]);

  const dirtyState = useBoundaryDraftDirtyState({
    layerRef: mapLevelDraftLayerRef,
    draftFeatures,
    draftFeaturesRef,
    getBoundaryId: getPreviewBoundaryId,
    isNewDraftId,
    compareMode: BASELINE_COMPARE_MODE,
    isValidLiveGeometry: isValidPreviewPolygonGeometry,
  });

  const syncDraftSourceToMapCallback = useCallback(() => {
    syncDraftSourceToMap(
      mapRef.current?.getMap(),
      MAP_LEVEL_DRAFT_SOURCE_ID,
      draftFeaturesRef.current,
    );
  }, [mapRef]);

  const setDraftFeaturesState = useCallback(
    (
      updater:
        | GeoJSON.Feature<GeoJSON.Polygon>[]
        | ((prev: GeoJSON.Feature<GeoJSON.Polygon>[]) => GeoJSON.Feature<GeoJSON.Polygon>[]),
    ) => {
      setDraftFeatures((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        draftFeaturesRef.current = next;
        return next;
      });
    },
    [],
  );

  const pruneUnchangedDraftFeatures = useCallback(() => {
    dirtyState.pruneUnchangedDraftFeatures({
      removeFromDraftState: (idsToRemove) => {
        setDraftFeaturesState((prev) =>
          prev.filter((feature) => !idsToRemove.includes(getPreviewBoundaryId(feature))),
        );
      },
      onAfterPrune: syncDraftSourceToMapCallback,
    });
  }, [dirtyState, setDraftFeaturesState, syncDraftSourceToMapCallback]);

  const draftBoundaryIds = useMemo(
    () =>
      draftFeatures
        .filter((f) => !isNewDraftId(getPreviewBoundaryId(f)))
        .map(getPreviewBoundaryId)
        .filter(Boolean),
    [draftFeatures],
  );

  const draftGeoJson = useMemo(
    (): GeoJSON.FeatureCollection<GeoJSON.Polygon> => toVisibleDraftGeoJson(draftFeatures),
    [draftFeatures],
  );

  const getTileLayerFilter = useCallback((): maplibregl.FilterSpecification | undefined => {
    if (draftBoundaryIds.length === 0) {
      return undefined;
    }

    return ["!", ["in", ["get", "id"], ["literal", draftBoundaryIds]]] as maplibregl.FilterSpecification;
  }, [draftBoundaryIds]);

  const upsertToDraft = useCallback(
    (feature: GeoJSON.Feature<GeoJSON.Polygon>) => {
      const id = getPreviewBoundaryId(feature);
      if (!id) {
        return;
      }

      dirtyState.captureBaseline(feature);

      setDraftFeaturesState((prev) => {
        const exists = prev.some((f) => getPreviewBoundaryId(f) === id);
        if (exists) {
          return prev.map((f) => (getPreviewBoundaryId(f) === id ? { ...feature, id } : f));
        }

        return [...prev, { ...feature, id, properties: { ...feature.properties, id } }];
      });
      syncDraftSourceToMapCallback();
    },
    [dirtyState, setDraftFeaturesState, syncDraftSourceToMapCallback],
  );

  const scheduleDraftPolygonEdit = useCallback(
    (boundaryId: string) => {
      queueMicrotask(() => {
        startDraftPolygonEditWhenReady({
          draftLayerRef: mapLevelDraftLayerRef,
          tileLayerRef: mapLevelBoundariesLayerRef,
          boundaryId,
          syncDraftToMap: syncDraftSourceToMapCallback,
        });
      });
    },
    [mapLevelBoundariesLayerRef, mapLevelDraftLayerRef, syncDraftSourceToMapCallback],
  );

  const getDraftFeatureByBoundaryId = useCallback(
    (boundaryId: string): GeoJSON.Feature<GeoJSON.Polygon> | null => {
      const idx = findDraftFeatureIndex(draftFeaturesRef.current, boundaryId);
      if (idx === -1) {
        return null;
      }
      return draftFeaturesRef.current[idx] ?? null;
    },
    [],
  );

  const addDrawnFeatureToDraft = useCallback(
    (feature: GeoJSON.Feature<GeoJSON.Polygon>, onAdded?: (newFeatureId: string) => void) => {
      const id = feature.properties?.id ?? `draft-${Date.now()}-${randomHex()}`;
      const normalizedFeature: GeoJSON.Feature<GeoJSON.Polygon> = {
        ...feature,
        id,
        properties: {
          ...feature.properties,
          id,
          mapLevelId,
        },
      };

      setDraftFeaturesState((prev) => [...prev, normalizedFeature]);
      dirtyState.markBoundaryUserTouched(String(id));
      syncDraftSourceToMapCallback();
      onAdded?.(String(id));
    },
    [dirtyState, mapLevelId, setDraftFeaturesState, syncDraftSourceToMapCallback],
  );

  const deleteInDraft = useCallback(
    (
      polygonId: string | number,
      onAfter?: (nextVisible: GeoJSON.FeatureCollection<GeoJSON.Polygon>) => void,
    ) => {
      const id = String(polygonId);
      setDraftFeaturesState((prev) => {
        const index = findDraftFeatureIndex(prev, id);
        if (index === -1) {
          return prev;
        }

        const featureId = getPreviewBoundaryId(prev[index]);
        let next: GeoJSON.Feature<GeoJSON.Polygon>[];
        if (isNewDraftId(featureId)) {
          dirtyState.removeDirtyStateForId(featureId);
          next = prev.filter((_, i) => i !== index);
        } else {
          dirtyState.markBoundaryUserTouched(featureId);
          next = prev.map((f, i) =>
            i === index ? { ...f, properties: { ...f.properties, deleted: true } } : f,
          );
        }

        syncDraftSourceToMapCallback();
        queueMicrotask(() => onAfter?.(toVisibleDraftGeoJson(next)));
        return next;
      });
    },
    [dirtyState, setDraftFeaturesState, syncDraftSourceToMapCallback],
  );

  const removeFromDraft = useCallback(
    (boundaryId: string) => {
      dirtyState.removeDirtyStateForId(boundaryId);
      setDraftFeaturesState((prev) =>
        prev.filter((f) => getPreviewBoundaryId(f) !== boundaryId),
      );
      syncDraftSourceToMapCallback();
    },
    [dirtyState, setDraftFeaturesState, syncDraftSourceToMapCallback],
  );

  const clearDraft = useCallback(() => {
    dirtyState.clearAllDirtyState();
    setDraftFeaturesState([]);
    syncDraftSourceToMapCallback();
    dispatch(
      mapLevelsApi.util.invalidateTags([
        { type: ApiTags.MapLevels, id: `geometries-${mapLevelId}` },
      ]),
    );
  }, [dirtyState, dispatch, mapLevelId, setDraftFeaturesState, syncDraftSourceToMapCallback]);

  const clearDraftLayerActiveState = useCallback(
    (clearLayerActiveOptions?: { endDrawSession?: boolean }) => {
      mapLevelDraftLayerRef.current?.stopEdit({
        endDrawSession: clearLayerActiveOptions?.endDrawSession,
      });
      mapLevelDraftLayerRef.current?.clearActivePolygon();
    },
    [mapLevelDraftLayerRef],
  );

  const persistCurrentEditing = dirtyState.createPersistCurrentEditing({
    buildFeatureFromLive: (live, existing) =>
      buildMapLevelBoundaryDraftFeatureFromLive(live, existing, mapLevelId),
    upsertToDraft,
    removeFromDraft,
    getExistingFeature: getDraftFeatureByBoundaryId,
    pruneAfterPersist: pruneUnchangedDraftFeatures,
  });

  const saveDraft = useCallback(async () => {
    const featuresToSave = dirtyState.getDirtyFeatures();
    if (featuresToSave.length === 0) {
      return;
    }

    const boundaries = featuresToSave.map((feature): IUpsertMapLevelBoundaryItem => {
      const id = getPreviewBoundaryId(feature);
      const isNew = isNewDraftId(id);
      const props = feature.properties as Record<string, unknown> | undefined;
      const name = typeof props?.name === "string" && props.name !== "" ? props.name : undefined;
      const description = descriptionForUpsert(props);

      return {
        id: isNew ? null : id,
        geometry: feature.geometry,
        deleted: Boolean(props?.deleted),
        ...(name != null ? { name } : {}),
        ...(description !== undefined ? { description } : {}),
      };
    });

    try {
      const batches = chunkByPayloadSize(
        boundaries,
        BOUNDARY_UPSERT_MAX_BATCH_BYTES,
        estimateJsonByteSize,
      );

      await runSequentialBatches(batches, async (batch) => {
        await upsertBoundaries({
          districtId,
          levelId: mapLevelId,
          body: { boundaries: batch },
        }).unwrap();
      });

      showToast({
        message: dictionaries.mapPage.layersVisibility.boundariesSaved,
        type: "success",
      });
      clearDraft();
      onSaveSuccess?.();
    } catch {
      showToast({
        message: dictionaries.mapPage.layersVisibility.boundariesSaveError,
        type: "error",
      });
      throw new Error("saveDraft failed");
    }
  }, [clearDraft, dirtyState, districtId, mapLevelId, onSaveSuccess, showToast, upsertBoundaries]);

  const ensureBoundaryInDraftFromId = useCallback(
    async (
      boundaryId: string,
      lookup?: { mapPolygonId?: string | number },
    ): Promise<MapLevelDraftSelection | null> => {
      persistCurrentEditing();

      // Already in the edit draft — keep geometry + Name/Note; do not rebuild from cache.
      const existingDraft = getDraftFeatureByBoundaryId(boundaryId);
      if (existingDraft != null) {
        scheduleDraftPolygonEdit(boundaryId);
        return selectionFromFeature(existingDraft, mapLevelId);
      }

      const lookupId = lookup?.mapPolygonId ?? boundaryId;
      const tileFeature =
        lookupId != null && lookupId !== ""
          ? (mapLevelBoundariesLayerRef.current?.getPolygonById(lookupId) ?? null)
          : null;

      let featureToAdd: GeoJSON.Feature<GeoJSON.Polygon> | null = null;

      const cachedGeometryGeoJson = getCachedGeometry(boundaryId);
      if (cachedGeometryGeoJson != null) {
        const savedBoundary = mapLevel?.boundaries?.find((b) => b.id === boundaryId) ?? null;
        featureToAdd = featureFromMapLevelGeometry({
          id: boundaryId,
          geometryGeoJson: cachedGeometryGeoJson,
          mapLevelId,
          baseFeature: tileFeature,
          savedMeta: savedBoundary,
        });
      }

      if (!featureToAdd) {
        showToast({
          message: dictionaries.mapPage.layersVisibility.boundariesSaveError,
          type: "error",
        });
        return null;
      }

      upsertToDraft(featureToAdd);
      const addedBoundaryId = getPreviewBoundaryId(featureToAdd);
      if (addedBoundaryId) {
        scheduleDraftPolygonEdit(addedBoundaryId);
      }

      return selectionFromFeature(featureToAdd, mapLevelId);
    },
    [
      getCachedGeometry,
      getDraftFeatureByBoundaryId,
      mapLevel?.boundaries,
      mapLevelId,
      persistCurrentEditing,
      mapLevelBoundariesLayerRef,
      scheduleDraftPolygonEdit,
      showToast,
      upsertToDraft,
    ],
  );

  const handleTilePolygonClick = useCallback(
    async (polygonData: PolygonData): Promise<MapLevelDraftSelection | null> => {
      const boundaryId = parseMapLevelBoundaryFeatureId(polygonData);
      if (!boundaryId) {
        showToast({
          message: dictionaries.mapPage.layersVisibility.boundariesSaveError,
          type: "error",
        });
        return null;
      }

      const mapPolygonId = polygonData.id ?? (polygonData.value as string | number);
      return ensureBoundaryInDraftFromId(boundaryId, { mapPolygonId });
    },
    [ensureBoundaryInDraftFromId, showToast],
  );

  const handleDraftPolygonClick = useCallback(
    async (polygonData: PolygonData): Promise<MapLevelDraftSelection | null> => {
      const polygonId = polygonData.id ?? (polygonData.value as string | number);
      if (polygonId == null || polygonId === "") {
        return null;
      }

      const editingId = mapLevelDraftLayerRef.current?.editingIdentifier?.value;
      const isSameAsCurrentEdit =
        editingId != null && String(editingId) === String(polygonId);

      if (!isSameAsCurrentEdit) {
        persistCurrentEditing();
      }

      const polygonIdStr = String(polygonId);
      if (!isSameAsCurrentEdit) {
        scheduleDraftPolygonEdit(polygonIdStr);
      }

      const feature = mapLevelDraftLayerRef.current?.getPolygonById(polygonId);
      if (!feature || !isValidPreviewPolygonGeometry(feature.geometry)) {
        return { boundaryId: polygonIdStr, mapLevelId };
      }

      return selectionFromFeature(feature, mapLevelId);
    },
    [
      mapLevelId,
      persistCurrentEditing,
      mapLevelDraftLayerRef,
      scheduleDraftPolygonEdit,
    ],
  );

  const getGapFillDraftOverrides = useCallback((): IGapFillDraftOverrides => {
    return mapFeaturesToGapFillDraftOverrides(
      dirtyState.getDirtyFeaturesIncludingLiveEditing(),
      getPreviewBoundaryId,
    );
  }, [dirtyState]);

  /**
   * Same pattern as work-map / attendance `updateBoundarySchool`:
   * write Name/Note onto the draft feature only; Save persists, Cancel discards.
   */
  const updateBoundaryMetadata = useCallback(
    (boundaryId: string, patch: { name?: string; note?: string }) => {
      if (patch.name == null && patch.note == null) {
        return;
      }

      const trimmedName = patch.name?.trim();
      const descriptionValue =
        patch.note != null
          ? toMapLevelBoundaryDescriptionUpsertValue(patch.note)
          : undefined;

      dirtyState.markBoundaryUserTouched(boundaryId);

      setDraftFeaturesState((prev) => {
        const idx = findDraftFeatureIndex(prev, boundaryId);
        if (idx === -1) {
          return prev;
        }

        const feature = prev[idx];
        const props = (feature.properties ?? {}) as Record<string, unknown>;
        const newProps = {
          ...props,
          ...(trimmedName != null && trimmedName !== "" ? { name: trimmedName } : {}),
          ...(patch.note != null ? { description: descriptionValue ?? "" } : {}),
        };

        const updated: GeoJSON.Feature<GeoJSON.Polygon> = {
          ...feature,
          properties: newProps,
        };

        mapLevelDraftLayerRef.current?.updateProperties(boundaryId, {
          ...(trimmedName != null && trimmedName !== "" ? { name: trimmedName } : {}),
          ...(patch.note != null ? { description: descriptionValue ?? "" } : {}),
        });

        return prev.map((f, i) => (i === idx ? updated : f));
      });
    },
    [dirtyState, mapLevelDraftLayerRef, setDraftFeaturesState],
  );

  return {
    draftGeoJson,
    hasDraftChanges: dirtyState.hasDraftChanges,
    getHasDraftChanges: dirtyState.getHasDraftChanges,
    draftBoundaryIds,
    getTileLayerFilter,
    upsertToDraft,
    addDrawnFeatureToDraft,
    deleteInDraft,
    removeFromDraft,
    clearDraft,
    clearDraftLayerActiveState,
    saveDraft,
    persistCurrentEditing,
    ensureBoundaryInDraftFromId,
    handleTilePolygonClick,
    handleDraftPolygonClick,
    getDraftFeatureByBoundaryId,
    syncDraftSourceToMap: syncDraftSourceToMapCallback,
    handlePolygonEditStart: dirtyState.handlePolygonEditStart,
    handleDrawUpdate: dirtyState.handleDrawUpdate,
    markBoundaryUserTouched: dirtyState.markBoundaryUserTouched,
    updateBoundaryMetadata,
    getGapFillDraftOverrides,
  };
}
